import React, { useEffect, useState } from 'react';
import { shift } from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  ContainerInput,
  InputDate,
  CloseIcon,
  MiddleText,
  ContainerText,
  DatePickerPopperGlobalStyles,
} from './styles';

const popperAvoidClipping = {
  strategy: 'fixed' as const,
};

/** Keeps calendar inside the viewport on narrow screens (Floating UI runs after default flip/offset/arrow). */
const datePickerPopperModifiers = [
  shift({ padding: { top: 12, bottom: 12, left: 12, right: 12 }, crossAxis: true }),
];

/**
 * Dashboard custom range: years from (currentYear - 29) through (currentYear + 1).
 * Example when today is in 2026: 1997 … 2027 (inclusive).
 */
function getDropdownPickerYearBounds() {
  const y = new Date().getFullYear();
  return {
    minDate: new Date(y - 29, 0, 1),
    maxDate: new Date(y + 1, 11, 31, 23, 59, 59, 999),
  };
}

export default ({
  date,
  setDate,
  endDate,
  setEndDate,
  handleStatus,
  closeIcon = true,
  showRange = false,
  minEndDate,
  dropdownPicker,
}: {
  date: Date;
  setDate: Function;
  endDate?: Date;
  setEndDate?: any;
  handleStatus?: any;
  closeIcon?: boolean;
  showRange?: boolean;
  minEndDate?: Date;
  dropdownPicker?: boolean;
}) => {
  const yearBounds = dropdownPicker ? getDropdownPickerYearBounds() : null;
  const [popperPlacement, setPopperPlacement] = useState<Placement>('bottom');

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const sync = () => setPopperPlacement(mq.matches ? 'bottom-start' : 'bottom');
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return (
    <ContainerInput>
      {dropdownPicker ? <DatePickerPopperGlobalStyles /> : null}
      <InputDate>
        <DatePicker
          selected={date}
          startDate={date}
          popperPlacement={dropdownPicker ? popperPlacement : 'bottom'}
          popperProps={dropdownPicker ? popperAvoidClipping : undefined}
          popperModifiers={dropdownPicker ? datePickerPopperModifiers : undefined}
          onChange={date => setDate(date)}
          endDate={showRange ? endDate : undefined}
          selectsStart={showRange}
          showMonthDropdown={dropdownPicker ? true : false}
          showYearDropdown={dropdownPicker ? true : false}
          dropdownMode="scroll"
          scrollableYearDropdown
          yearDropdownItemNumber={20}
          showDisabledMonthNavigation
          minDate={yearBounds?.minDate}
          maxDate={yearBounds?.maxDate}
        />
      </InputDate>
      {endDate && (
        <>
          <ContainerText>
            <MiddleText> TO </MiddleText>
          </ContainerText>
          <InputDate>
            <DatePicker
              selectsEnd
              selected={endDate}
              popperPlacement={dropdownPicker ? popperPlacement : 'bottom'}
              popperProps={dropdownPicker ? popperAvoidClipping : undefined}
              popperModifiers={dropdownPicker ? datePickerPopperModifiers : undefined}
              startDate={showRange ? date : undefined}
              endDate={showRange ? endDate : undefined}
              minDate={
                yearBounds
                  ? minEndDate
                    ? new Date(Math.max(minEndDate.getTime(), yearBounds.minDate.getTime()))
                    : yearBounds.minDate
                  : minEndDate
                  ? minEndDate
                  : undefined
              }
              maxDate={yearBounds?.maxDate}
              onChange={endDate => setEndDate(endDate)}
              showMonthDropdown={dropdownPicker ? true : false}
              showYearDropdown={dropdownPicker ? true : false}
              dropdownMode="scroll"
              scrollableYearDropdown
              yearDropdownItemNumber={20}
              showDisabledMonthNavigation
            />
          </InputDate>
        </>
      )}
      {closeIcon && (
        <CloseIcon onClick={handleStatus} color={'rgba(93,110,129,1)'} size={'25px'} />
      )}
    </ContainerInput>
  );
};
